import {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@core/services/auth.service';
import { TokenService } from '@core/services/token.service';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { TokenConfig, TOKENS } from '../types';
import { SessionService } from '../services/session.service';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

// const USER_PROFILE_RETRY_ATTEMPTED = new HttpContextToken<boolean>(() => false);
let isRedirectingToDrm = false;

export const RefreshTokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  let isRefreshing = false;
  const refreshTokenSubject = new BehaviorSubject<TokenConfig | boolean | null>(
    null,
  );

  const service = inject(SessionService);
  const tokenService = inject(TokenService);
  const authService = inject(AuthService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((err) => {
      // Session Timeout
      if (err.status === 408) {
        service.clearSession(true);
        tokenService.logoutCred();
        return throwError(() => err);
      }

      if (err.status === 403) {
        void router.navigate(['/access-denied']);
      }

      // Skip interceptor logic for these endpoints
      if (
        req.url.includes('refresh-token') ||
        req.url.includes('user/login') ||
        req.url.includes('logout')
      ) {
        return throwError(() => err);
      }

      // If the error is not a 401, just pass it along
      if (err.status !== 401) {
        return throwError(() => err);
      }

      // Check if we have a refresh token to attempt refresh
      const isCookieSession = localStorage.getItem('isCookiesLogin') === 'true';

      // If no refresh token or navigation fails, logout
      if (
        !isCookieSession ||
        (req.url.includes('navigation') && !isCookieSession)
      ) {
        redirectToDrm();
        return throwError(() => err);
      }

      // If the token is already being refreshed, queue the requests
      if (isRefreshing) {
        return refreshTokenSubject.pipe(
          filter((result) => result !== null),
          take(1),
          switchMap(() => next(addAuthenticationToken(req))),
        );
      } else {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        if (isCookieSession) {
          return authService.refreshTokenCookie().pipe(
            tap(() => {
              isRefreshing = false;
              refreshTokenSubject.next(true);
            }),
            switchMap(() => next(addAuthenticationToken(req))),
            catchError((error) => {
              isRefreshing = false;
              redirectToDrm();
              return throwError(() => error);
            }),
          );
        } else {
          redirectToDrm();
          return throwError(() => err);
        }
      }
    }),
  );

  function redirectToDrm() {
    if (isRedirectingToDrm) return;

    isRedirectingToDrm = true;
    const redirectUrl = `${window.location.origin}`;
    window.location.href = `${environment.drm_url}/auth/login?redirectUrl=${encodeURIComponent(
      redirectUrl,
    )}`;
  }

  function addAuthenticationToken(request: HttpRequest<unknown>) {
    const accessToken = tokenService.getAuthToken(TOKENS.accessToken);
    if (!accessToken) {
      return request;
    }

    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  }
};
